import React, { useState, useEffect } from 'react';
import { List, Avatar, Spin, Empty, Typography } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { getResidents } from '@/api/residents';
import { Resident } from '@/types/resources/resident';

const { Text } = Typography;

interface ResidentListTabProps {
  onSelect: (resident: Resident) => void;
  searchTerm: string;
  excludeAssigned?: boolean; // New prop to exclude assigned residents
}

export const ResidentListTab: React.FC<ResidentListTabProps> = ({ 
  onSelect, 
  searchTerm, 
  excludeAssigned = false 
}) => {
  const { t } = useTranslation('residents');
  const { firestoreUser } = useAuth();
  const [residents, setResidents] = useState<Resident[]>([]);
  const [filteredResidents, setFilteredResidents] = useState<Resident[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load residents
  useEffect(() => {
    if (!firestoreUser?.workspace?.id) return;

    const loadResidents = async () => {
      try {
        setIsLoading(true);
        const residentsData = await getResidents({
          firestoreUser
        });
        setResidents(residentsData.residents);
      } catch (error) {
        console.error('Error loading residents:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadResidents();
  }, [firestoreUser?.workspace?.id]);

  // Filter residents based on search term and assignment status
  useEffect(() => {
    if (!residents || !Array.isArray(residents)) {
      setFilteredResidents([]);
      return;
    }
    
    let filtered = residents;
    
    // Filter by assignment status if excludeAssigned is true
    if (excludeAssigned) {
      filtered = filtered.filter(resident => !resident.apartmentUnit?.id);
    }
    
    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(resident =>
        resident?.user?.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (resident?.contacts && resident.contacts.length > 0 && resident.contacts[0]?.name?.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    setFilteredResidents(filtered);
  }, [residents, searchTerm, excludeAssigned]);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">{t('loading', 'Loading residents...')}</Text>
        </div>
      </div>
    );
  }

  if (filteredResidents.length === 0) {
    return (
      <Empty
        description={
          excludeAssigned 
            ? t('noUnassignedResidents', 'No unassigned residents found')
            : searchTerm.trim() 
              ? t('noResidentsFound', 'No residents found matching your search')
              : t('noResidents', 'No residents found in this workspace')
        }
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <List
      itemLayout="horizontal"
      dataSource={filteredResidents}
      style={{ maxHeight: '400px', overflowY: 'auto' }}
      renderItem={(resident) => {
        if (!resident) return null;
        
        return (
          <List.Item
            onClick={() => onSelect(resident)}
          style={{
            padding: '12px 16px',
            cursor: 'pointer',
            borderBottom: '1px solid #f0f0f0',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f5f5f5';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <List.Item.Meta
            avatar={
              <Avatar 
                icon={<UserOutlined />}
                size="large"
              />
            }
            title={
              <Text strong style={{ fontSize: '16px' }}>
                {resident.user?.displayName || (resident.contacts && resident.contacts.length > 0 ? resident.contacts[0]?.name : 'Resident')}
              </Text>
            }
            description={
              <div>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  {resident.contacts && resident.contacts.length > 0 ? resident.contacts[0]?.email || 'No email' : 'No email'}
                </Text>
                {resident.contacts && resident.contacts.length > 0 && resident.contacts[0]?.phone && (
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      📞 {resident.contacts[0].phone}
                    </Text>
                  </div>
                )}
                {excludeAssigned && resident.apartmentUnit && (
                  <div>
                    <Text type="warning" style={{ fontSize: '12px' }}>
                      ⚠️ Currently assigned to {resident.apartmentUnit.number}
                    </Text>
                  </div>
                )}
              </div>
            }
          />
        </List.Item>
        );
      }}
    />
  );
};
